import React, { useState, useEffect, useCallback, useRef } from 'react';
import Preloader from '../components/Preloader';
import SearchableSelect from '../components/SearchableSelect';
import { Navigate } from 'react-router-dom';
import { useCheckUserRole } from './hooks/useCheckUserRole';

const YabanciAcilis = () => {
    const { userRole, isLoggedIn, subRole, userId } = useCheckUserRole();

    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    // Advanced Filter Modes: { [colName]: 'default' | 'range' | 'lt' | 'gt' | 'eq' }
    const [filterModes, setFilterModes] = useState({});

    // Range Tolerance: { [colName]: { minus: 0.15, plus: 0.20 } }
    const [rangeTolerance, setRangeTolerance] = useState({});
    const [tolerancePopup, setTolerancePopup] = useState(null);
    const [toleranceInputs, setToleranceInputs] = useState({ minus: '', plus: '' });

    const [bookmakers, setBookmakers] = useState([]);
    const [selectedBookmakerName, setSelectedBookmakerName] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [matchesForDropdown, setMatchesForDropdown] = useState([]);
    const [selectedMatchId, setSelectedMatchId] = useState('');

    const [savedFilters, setSavedFilters] = useState([]);
    const [selectedFilterId, setSelectedFilterId] = useState('');

    const [showSettings, setShowSettings] = useState(false);
    const [columnGroups, setColumnGroups] = useState([]);

    // --- State: Design Settings ---
    const defaultColors = {
        headerBg: '#0a1b2a',
        headerText: '#F6F1AD',
        rowOddBg: '#1f2d3d',
        rowEvenBg: '#152232',
        rowText: '#F6F1AD',
        hoverBg: '#2a4a6a'
    };

    const colorLabels = {
        headerBg: 'Başlık Arkaplanı',
        headerText: 'Başlık Yazısı',
        rowOddBg: 'Satır (Tek) Arkaplanı',
        rowEvenBg: 'Satır (Çift) Arkaplanı',
        rowText: 'Satır Yazısı',
        hoverBg: 'Üzerine Gelince'
    };

    const [colors, setColors] = useState(() => {
        const savedColors = localStorage.getItem('bookmakerColors');
        return savedColors ? JSON.parse(savedColors) : defaultColors;
    });

    // --- Data Fetching: Init ---
    const generateDateOptions = () => {
        const options = [];
        const todayDate = new Date();
        const dayLabels = ['Bugün', 'Yarın', 'Ertesi Gün'];
        for (let i = 0; i <= 2; i++) {
            const d = new Date(todayDate);
            d.setDate(todayDate.getDate() + i);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const dateStr = `${day}.${month}.${year}`;
            options.push({ value: dateStr, label: `${dayLabels[i]} (${dateStr})` });
        }
        return options;
    };
    const [dateOptions] = useState(generateDateOptions());

    useEffect(() => {
        const fetchBookmakers = async () => {
            try {
                const response = await fetch('/api/bookmaker-tables');
                if (response.ok) {
                    const data = await response.json();
                    setBookmakers(data);
                }
            } catch (err) {
                console.error("Failed to fetch bookmakers", err);
            }
        };
        fetchBookmakers();
    }, []);

    // --- Column Modal State ---
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [allPotentialGroups, setAllPotentialGroups] = useState([]);
    const [tempSelectedIds, setTempSelectedIds] = useState([]);

    // Fetch all potential groups for the modal (userId=0)
    useEffect(() => {
        const fetchAllGroups = async () => {
            try {
                const response = await fetch(`/api/Columns/Get/B/0`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.isSuccess) setAllPotentialGroups(data.groups);
                }
            } catch (err) { console.error(err); }
        };
        fetchAllGroups();
    }, []);

    // Reusable fetch columns function
    const fetchColumns = useCallback(async () => {
        if (!userId) return; // Wait for userId to be loaded
        try {
            const response = await fetch(`/api/Columns/Get/B/${userId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.isSuccess) {
                    setColumnGroups(data.groups);
                }
            }
        } catch (err) {
            console.error("Failed to fetch columns", err);
        }
    }, [userId]);

    useEffect(() => {
        fetchColumns();
    }, [fetchColumns]);

    const openColumnModal = () => {
        // Init temp selection with currently active groups
        const currentIds = columnGroups.map(g => g.id);
        // Ensure ID 1 is included
        if (!currentIds.includes(1)) currentIds.push(1);
        setTempSelectedIds(currentIds);
        setShowColumnModal(true);
    };

    const toggleGroupSelection = (id) => {
        const iId = intId(id);
        if (iId === 1) return; // Prevent toggling ID 1

        if (tempSelectedIds.includes(iId)) {
            setTempSelectedIds(tempSelectedIds.filter(x => x !== iId));
        } else {
            setTempSelectedIds([...tempSelectedIds, iId]);
        }
    };

    const handleSelectAll = () => {
        setTempSelectedIds(allPotentialGroups.map(g => intId(g.id)));
    };

    const handleDeselectAll = () => {
        // Always keep ID 1
        setTempSelectedIds([1]);
    };

    // ... (rendering part change below)

    // NOTE: I will apply the rendering change in a separate chunk or same chunk if contiguous. 
    // Since they are far apart, I need multi_replace. Or I can do two calls. 
    // I'll do two replace calls for safety as they are far. This one is for logic.


    const handleSaveColumnConfig = async () => {
        if (!userId) {
            alert("Kullanıcı kimliği bulunamadı.");
            return;
        }
        // If empty, maybe alert? Or allow clearing all?
        const idStr = tempSelectedIds.join(',');
        try {
            const response = await fetch(`/api/Columns/Save/B/${userId}/${idStr}`, {
                method: 'POST'
            });
            const data = await response.json();
            if (data.isSuccess) {
                alert("Kolon ayarları kaydedildi ve güncellendi.");
                setShowColumnModal(false);
                fetchColumns(); // Update the main table immediately
            } else {
                alert("Hata: " + data.message);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Helper to ensure comparison is safe (string vs int)
    const intId = (val) => parseInt(val, 10);

    // --- Saved Filters ---
    const fetchSavedFilters = useCallback(async () => {
        try {
            const response = await fetch('/api/get_filters?filter_type=3');
            if (response.ok) {
                const data = await response.json();
                setSavedFilters(data);
            }
        } catch (error) {
            console.error("Filters fetch error:", error);
        }
    }, []);

    useEffect(() => {
        fetchSavedFilters();
    }, [fetchSavedFilters]);

    const handleSaveSavedFilter = async () => {
        const filterName = prompt("Filtre adı giriniz:");
        if (!filterName) return;

        const filterDataToSave = {
            filters,
            filterModes,
            rangeTolerance,
        };

        try {
            const response = await fetch('/api/save-filter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filter_name: filterName,
                    filter_data: filterDataToSave,
                    filter_type: 3
                }),
            });

            if (response.ok) {
                alert("Filtre başarıyla kaydedildi.");
                fetchSavedFilters();
            } else {
                const err = await response.json();
                alert("Hata: " + (err.error || "Kaydedilemedi"));
            }
        } catch (error) {
            console.error("Save error:", error);
            alert("Bir hata oluştu.");
        }
    };

    const handleDeleteSavedFilter = async () => {
        if (!selectedFilterId) return;
        if (!window.confirm("Bu filtreyi silmek istediğinize emin misiniz?")) return;

        try {
            const response = await fetch('/api/delete-filter', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: selectedFilterId }),
            });

            if (response.ok) {
                alert("Filtre silindi.");
                setSelectedFilterId("");
                fetchSavedFilters();
            } else {
                alert("Silinemedi.");
            }
        } catch (e) {
            console.error(e);
            alert("Hata oluştu.");
        }
    };

    // --- Match Fetching ---
    const getTableId = useCallback(() => {
        const bm = bookmakers.find(b => b.name === selectedBookmakerName);
        return bm ? bm.id : null;
    }, [bookmakers, selectedBookmakerName]);

    // Fetch matches for DROPDOWN when bookmaker/date changes
    useEffect(() => {
        const fetchDropdownMatches = async () => {
            // Reset selections on date/bookmaker change
            setSelectedMatchId('');
            setSelectedFilterId('');

            const tId = getTableId();
            if (!tId || !selectedDate) {
                setMatchesForDropdown([]);
                return;
            }

            try {
                const parts = selectedDate.split('.');
                let formattedDate = selectedDate;
                if (parts.length === 3) {
                    formattedDate = `${parts[2]}${parts[1]}${parts[0]}`; // YYYYMMDD
                }

                const response = await fetch(`/api/Match/Get/${tId}/${formattedDate}?type=init`);
                const data = await response.json();
                if (data.isSuccess) {
                    setMatchesForDropdown(data.matches);
                }
            } catch (e) {
                console.error(e);
            }
        };
        fetchDropdownMatches();
    }, [selectedBookmakerName, selectedDate, getTableId]);

    // Refactored helper to apply a filter (by ID) given a specific match ID
    const checkAndApplyFilter = (filterId, matchId) => {
        if (!filterId) return;
        const savedF = savedFilters.find(f => f.id.toString() === filterId.toString());
        if (!savedF) return;

        let pData = savedF.filter_data;
        if (typeof pData === 'string') {
            try { pData = JSON.parse(pData); } catch (ex) { }
        }

        // Apply context from saved filter if needed (bookmaker, date) - though usually user sets these first
        if (pData.tableId && !selectedBookmakerName) {
            const bm = bookmakers.find(b => b.id.toString() === pData.tableId.toString());
            if (bm) setSelectedBookmakerName(bm.name);
        }
        if (pData.match_date && !selectedDate) setSelectedDate(pData.match_date);

        setFilterModes(pData.filterModes || {});
        setRangeTolerance(pData.rangeTolerance || {});

        let filtersToSet = pData.filters;
        if (!filtersToSet && !pData.tableId) filtersToSet = pData;
        filtersToSet = filtersToSet || {};

        // If a match is selected, overlay its values onto the filter
        if (matchId && matchesForDropdown.length > 0) {
            const selectedMatch = matchesForDropdown.find(m => m.id.toString() === matchId.toString());
            if (selectedMatch && selectedMatch.full_data) {
                filtersToSet = { ...filtersToSet };

                // Comprehensive Blacklist of columns to EXCLUDE from "Same Match" filtering
                const blacklist = [
                    'id', 'match_id', 'sys_match_id',
                    'match_date', 'date', 'tarih', 'match_time', 'time', 'saat', 'mac_saati', 'mac_tarihi',
                    'home_team', 'away_team', 'team_home', 'team_away', 'year', 'yil',
                    'ev_sahibi', 'deplasman', 'ev', 'misafir', 'ev_takim', 'misafir_takim',
                    'home_name', 'away_name', 'home', 'away', 'match_name', 'mac_adi',
                    'league', 'lig', 'league_name', 'lig_adi', 'country', 'ulke', 'country_name',
                    'created_at', 'updated_at', 'inserted_at'
                ];

                // 1. Remove blacklisted keys from the Saved Filter explicitly
                blacklist.forEach(b => delete filtersToSet[b]);

                // 2. Update filter with match data, BUT SKIP blacklisted columns
                Object.keys(filtersToSet).forEach(key => {
                    if (blacklist.includes(key)) return;

                    const val = selectedMatch.full_data[key];
                    if (val !== undefined && val !== null) {
                        filtersToSet[key] = String(val);
                    }
                });
            }
        }
        setFilters(filtersToSet);
    };

    // Handle Match Selection from Dropdown
    const handleMatchSelect = async (e) => {
        const mId = e.target.value;

        // If cleared (empty ID), do NOTHING. 
        // This prevents re-fetching with "no match selected" state, keeping the previous table view.
        if (!mId) {
            setSelectedMatchId('');
            return;
        }

        setSelectedMatchId(mId);

        // If a filter is currently active, re-apply it with the new match's data
        if (selectedFilterId) {
            checkAndApplyFilter(selectedFilterId, mId);
        }

        // Trigger Local Filter Update Only - REMOVED to prevent table refresh/filtering by just ID
        // handleFilterChange('match_id', mId);
    };

    const abortControllerRef = useRef(null);

    const fetchMatches = useCallback(async (pageNum, currentFilters, currentTableId, currentDate) => {
        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (!currentTableId) {
            abortControllerRef.current = null;
            if (pageNum === 1) setMatches([]);
            setHasMore(false);
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        const signal = controller.signal;

        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (currentTableId) params.append('table_id', currentTableId);
            params.append('limit', 50);
            params.append('page', pageNum);

            if (currentDate) {
                const parts = currentDate.split('.');
                if (parts.length === 3) {
                    const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
                    params.append('match_date_eq', formatted);
                } else if (currentDate.includes('-')) {
                    params.append('match_date_eq', currentDate);
                }
            }

            Object.keys(currentFilters).forEach(key => {
                let val = currentFilters[key];
                if (val && val.trim() !== '') {
                    const mode = filterModes[key] || 'default';

                    // Special handling for legacy date columns (backend expects YYYY-MM-DD)
                    if (['match_date', 'match_time', 'match_d_t', 'date', 'Date', 'tarih'].includes(key)) {
                        if (val.includes('-')) {
                            const parts = val.split('-');
                            if (parts.length === 3) {
                                val = `${parts[2]}-${parts[1]}-${parts[0]}`;
                            }
                        } else if (val.includes('.')) {
                            const parts = val.split('.');
                            if (parts.length === 3) {
                                val = `${parts[2]}-${parts[1]}-${parts[0]}`;
                            }
                        }
                    }

                    if (mode === 'range') {
                        const parts = val.split('-');
                        if (parts.length === 2) {
                            params.append(key + '_min', parts[0].trim());
                            params.append(key + '_max', parts[1].trim());
                        } else {
                            params.append(key, val);
                        }
                    } else if (mode === 'lt') {
                        params.append(key + '_lt', val.trim());
                    } else if (mode === 'gt') {
                        params.append(key + '_gt', val.trim());
                    } else if (mode === 'eq') {
                        params.append(key + '_eq', val.trim());
                    } else {
                        // Default: Aralık filtresi (eksi/artı tolerans) veya normal ilike
                        const tol = rangeTolerance[key];
                        if (tol && (tol.minus > 0 || tol.plus > 0) && !isNaN(parseFloat(val))) {
                            const numVal = parseFloat(val);
                            params.append(key + '_min', (numVal - (tol.minus || 0)).toFixed(2));
                            params.append(key + '_max', (numVal + (tol.plus || 0)).toFixed(2));
                        } else {
                            params.append(key, val);
                        }
                    }
                }
            });

            const response = await fetch(`/api/bookmakers?${params.toString()}`, { signal });
            if (!response.ok) throw new Error('Network response was not ok');

            const data = await response.json();

            setMatches(prev => pageNum === 1 ? data.data : [...prev, ...data.data]);
            setHasMore(data.has_next);
        } catch (err) {
            if (err.name === 'AbortError') {
                console.log('Fetch aborted');
            } else {
                setError(err.message);
            }
        } finally {
            // Only set loading false if this is the active request (simplified check: unmounted or not aborted?)
            // Actually, if aborted, we technically don't need to unset loading if only one req active
            // But good practice is to check signal.aborted
            if (abortControllerRef.current === controller) {
                setLoading(false);
                abortControllerRef.current = null;
            }
        }
    }, [filterModes, rangeTolerance]);

    // --- Effects ---
    // 1. Debounce Filters
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const tId = getTableId();
            setPage(1);

            // If filters are active, ignore selectedDate to allow global history search
            // This ensures "Same Odds" analysis searches the whole timeframe, not just the selected day.
            // If no filters, show the selected day's program.
            const dateToUse = Object.keys(filters).length > 0 ? null : selectedDate;

            fetchMatches(1, filters, tId, dateToUse);
        }, 1000);
        return () => clearTimeout(timeoutId);
    }, [filters, selectedDate, selectedBookmakerName, getTableId, fetchMatches]);

    // 2. Page Change
    useEffect(() => {
        if (page > 1) {
            const tId = getTableId();
            const hasActiveFilters = Object.values(filters).some(
                (value) => String(value ?? '').trim() !== ''
            );
            fetchMatches(page, filters, tId, hasActiveFilters ? null : selectedDate);
        }
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    // 3. Infinite Scroll
    const lastMatchElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);


    // --- Handlers ---
    // Filter Mode Style Helper
    const getFilterModeStyle = (mode) => {
        switch (mode) {
            case 'eq':
                return {
                    bg: '#d4edda',
                    color: '#155724',
                    border: '1px solid #c3e6cb',
                    shadow: '0 0 0 2px rgba(40, 167, 69, 0.2)'
                };
            case 'range':
                return {
                    bg: '#cce5ff',
                    color: '#004085',
                    border: '1px solid #b8daff',
                    shadow: '0 0 0 2px rgba(0, 123, 255, 0.2)'
                };
            case 'lt':
                return {
                    bg: '#fff3cd',
                    color: '#856404',
                    border: '1px solid #ffeeba',
                    shadow: '0 0 0 2px rgba(255, 193, 7, 0.2)'
                };
            case 'gt':
                return {
                    bg: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb',
                    shadow: '0 0 0 2px rgba(220, 53, 69, 0.2)'
                };
            default:
                return {
                    bg: 'rgba(255,255,255,0.1)',
                    color: colors.headerText,
                    border: '1px solid transparent',
                    shadow: 'none'
                };
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleClearFilters = () => {
        setFilters({});
        setFilterModes({});
        setRangeTolerance({});
        setTolerancePopup(null);
        setSelectedDate('');
        setSelectedMatchId('');
        setSelectedFilterId('');
    };

    const handleColorChange = (key, value) => {
        const newColors = { ...colors, [key]: value };
        setColors(newColors);
        localStorage.setItem('bookmakerColors', JSON.stringify(newColors));
    };

    const handleFilterModeChange = (key, mode) => {
        setFilterModes(prev => ({ ...prev, [key]: mode }));
    };

    // --- Range Tolerance (±) ---

    const openTolerancePopup = (colName) => {
        if (tolerancePopup === colName) { setTolerancePopup(null); return; }
        const current = rangeTolerance[colName] || {};
        setToleranceInputs({ minus: current.minus || '', plus: current.plus || '' });
        setTolerancePopup(colName);
    };

    const applyTolerance = (colName) => {
        const minus = parseFloat(toleranceInputs.minus) || 0;
        const plus = parseFloat(toleranceInputs.plus) || 0;
        setRangeTolerance(prev => ({ ...prev, [colName]: { minus, plus } }));
        setTolerancePopup(null);
    };

    const clearTolerance = (colName) => {
        setRangeTolerance(prev => ({ ...prev, [colName]: { minus: 0, plus: 0 } }));
        setTolerancePopup(null);
    };





    const getCellContent = (match, colName) => {
        if (!match) return '-';

        // Format date columns
        if (['match_date', 'match_time', 'match_d_t'].includes(colName)) {
            const val = match[colName];
            if (!val) return '-';
            // If format is YYYY-MM-DD, convert to DD-MM-YYYY
            // Check if it matches YYYY-MM-DD pattern roughly
            if (typeof val === 'string' && val.includes('-')) {
                const parts = val.split('-');
                if (parts.length === 3) {
                    // Assuming YYYY-MM-DD
                    return `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
            }
            return val;
        }

        return match[colName] || '-';
    };





    if (!isLoggedIn || (userRole !== 'editor' && userRole !== 'admin' && !(userRole === 'user' && (subRole === 'oran' || subRole === 'full')))) {
        return <Navigate to="/login" />;
    }

    // --- TabloTest Style Rendering ---
    return (
        <>
            {loading && <div className="loader">
                <div className="loader-inner">
                    <div className="circle"></div>
                </div>
            </div>}
            {/* Breadcrumb Section */}
            <div className="breadcrumb-section breadcrumb-bg">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 offset-lg-2 text-center">
                            <div className="breadcrumb-text">
                                <p></p>
                                <h1>Açılış Tablosu</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bookmaker-container" style={{ marginTop: '64px' }}>
                <style>{`
                .premium-button {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0.3px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .premium-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0,0,0,0.4);
                }
                .premium-button:active {
                    transform: translateY(0);
                }
                .btn-save {
                    background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
                    color: white;
                }
                .btn-save:hover {
                    background: linear-gradient(135deg, #e85a2a 0%, #e08419 100%);
                }
                .btn-delete {
                    background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
                    color: #ff6b35;
                    border: 1px solid #ff6b35;
                }
                .btn-delete:hover {
                    background: linear-gradient(135deg, #3d3d3d 0%, #2a2a2a 100%);
                }
                .btn-delete:disabled {
                    background: linear-gradient(135deg, #3d3d3d 0%, #2a2a2a 100%);
                    color: #666;
                    border-color: #444;
                    cursor: not-allowed;
                    opacity: 0.6;
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                }
                .btn-delete:disabled:hover {
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                }
                .btn-clear {
                    background: linear-gradient(135deg, #3d3d3d 0%, #2a2a2a 100%);
                    color: #ffa500;
                    border: 1px solid #ffa500;
                }
                .btn-clear:hover {
                    background: linear-gradient(135deg, #4d4d4d 0%, #3a3a3a 100%);
                }
                .btn-settings {
                    background: linear-gradient(135deg, #ff8c00 0%, #ff6600 100%);
                    color: white;
                }
                .btn-settings:hover {
                    background: linear-gradient(135deg, #e67e00 0%, #e65c00 100%);
                }
                .btn-column {
                    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
                    color: #ff6b35;
                    border: 1px solid #ff6b35;
                }
                .btn-column:hover {
                    background: linear-gradient(135deg, #2a2a2a 0%, #3d3d3d 100%);
                }
                .premium-dropdown {
                    padding: 10px 16px;
                    border-radius: 10px;
                    border: 2px solid #ff6b35;
                    background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
                    font-size: 14px;
                    font-weight: 500;
                    color: #fff;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    min-width: 180px;
                }
                .premium-dropdown:hover {
                    border-color: #ffa500;
                    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
                }
                .premium-dropdown:focus {
                    outline: none;
                    border-color: #ffa500;
                    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.2);
                }
                .premium-dropdown option {
                    background: #1a1a1a;
                    color: #fff;
                }
                .divider {
                    width: 2px;
                    background: linear-gradient(180deg, transparent 0%, #ff6b35 50%, transparent 100%);
                    height: 35px;
                    margin: 0 8px;
                }
                .header-title {
                    background: linear-gradient(135deg, #ff6b35 0%, #ffa500 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                    margin: 0;
                    border: none;
                }

                @media (max-width: 768px) {
                    .responsive-controls {
                        flex-direction: column !important;
                        align-items: stretch !important;
                        gap: 16px !important;
                    }
                    .responsive-left-section {
                        flex-direction: column !important;
                        width: 100% !important;
                        gap: 12px !important;
                    }
                    .responsive-right-section {
                        flex-direction: column !important;
                        width: 100% !important;
                        justify-content: stretch !important;
                        gap: 10px !important;
                    }
                    .responsive-filter-item {
                        width: 100% !important;
                    }

                    .responsive-filter-item > div {
                        width: 100% !important;
                    }
                    .premium-button {
                        width: 100%;
                        justify-content: center;
                    }
                    .premium-dropdown {
                        width: 100% !important;
                    }
                    .divider {
                        display: none;
                    }
                }

                /* Scrollbar at TOP trick */
                .table-container {
                    transform: rotateX(180deg);
                    overflow-x: auto;
                    margin-bottom: 20px; /* Space for the scrollbar/header */
                }
                .bookmaker-table {
                    transform: rotateX(180deg);
                    width: 100%;
                    border-collapse: collapse;
                    font-family: 'Poppins', 'Segoe UI', sans-serif;
                }
                .bookmaker-table th,
                .bookmaker-table td {
                    text-align: center;
                    vertical-align: middle;
                    padding: 6px 10px;
                    font-size: 11px;
                    min-width: 80px;
                    border: 1px solid #000;
                }
                .bookmaker-table th {
                    font-weight: 600;
                    letter-spacing: 0.3px;
                }
                .bookmaker-table td {
                    font-weight: 500;
                }
            `}</style>

                {/* Header Row with Title */}
                {/* <div style={{
                    marginBottom: '16px',
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                }}>
                    <h2 className="header-title">🎯 Açılış Tablosu</h2>
                </div> */}


                {/* Controls Row - Split into Left and Right */}
                <div className="responsive-controls" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    gap: '20px',
                    flexWrap: 'wrap'
                }}>
                    {/* LEFT SECTION: Bookmaker + Date */}
                    <div className="responsive-left-section" style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: '0 1 auto' }}>
                        {/* Bookmaker Select */}
                        <div className="responsive-filter-item" style={{ width: '220px' }}>
                            <SearchableSelect
                                value={selectedBookmakerName}
                                onChange={(e) => setSelectedBookmakerName(e.target.value)}
                                options={bookmakers.map(b => ({ value: b.name, label: b.name }))}
                                placeholder="🏢 Büro Seçiniz"
                                name="bookmaker"
                                customOptionStyle={{
                                    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
                                    fontWeight: '700',
                                    fontSize: '15px'
                                }}
                            />
                        </div>

                        {/* Date Select - Only show when bookmaker is selected */}
                        {selectedBookmakerName && (
                            <div className="responsive-filter-item" style={{ width: '180px' }}>
                                <SearchableSelect
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    options={dateOptions}
                                    placeholder="🗓️ Tarih Seçiniz"
                                    name="date"
                                />
                            </div>
                        )}

                        {/* Match Select Dropdown - Moved inside Left Section */}
                        {selectedBookmakerName && selectedDate && (
                            <div className="responsive-filter-item" style={{ width: '250px' }}>
                                <SearchableSelect
                                    value={selectedMatchId}
                                    onChange={handleMatchSelect}
                                    options={matchesForDropdown.map(m => ({
                                        value: m.id,
                                        label: `${m.time} | ${m.country} | ${m.league} | ${m.name}`
                                    }))}
                                    placeholder="⚽ Maç Seçiniz"
                                    name="match"
                                />
                            </div>
                        )}
                    </div>


                    {/* RIGHT SECTION: Filters + Action Buttons */}
                    <div className="responsive-right-section" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', flex: '0 1 auto' }}>
                        {/* Statistics Dashboard */}


                        {/* Saved Filters Dropdown */}
                        <select
                            className="premium-dropdown"
                            value={selectedFilterId}
                            onChange={(e) => {
                                const fId = e.target.value;
                                setSelectedFilterId(fId);
                                if (fId) {
                                    const savedF = savedFilters.find(f => f.id.toString() === fId);
                                    if (savedF) {
                                        let pData = savedF.filter_data;
                                        if (typeof pData === 'string') {
                                            try { pData = JSON.parse(pData); } catch (ex) { }
                                        }
                                        if (pData.tableId) {
                                            const bm = bookmakers.find(b => b.id.toString() === pData.tableId.toString());
                                            if (bm) setSelectedBookmakerName(bm.name);
                                        }
                                        if (pData.match_date) setSelectedDate(pData.match_date);
                                        setFilterModes(pData.filterModes || {});
                                        setRangeTolerance(pData.rangeTolerance || {});
                                        let filtersToSet = pData.filters;
                                        if (!filtersToSet && !pData.tableId) filtersToSet = pData;
                                        filtersToSet = filtersToSet || {};

                                        if (selectedMatchId && matchesForDropdown.length > 0) {
                                            const selectedMatch = matchesForDropdown.find(m => m.id.toString() === selectedMatchId.toString());
                                            if (selectedMatch && selectedMatch.full_data) {
                                                filtersToSet = { ...filtersToSet };

                                                // Comprehensive Blacklist of columns to EXCLUDE from "Same Match" filtering
                                                const blacklist = [
                                                    'id', 'match_id', 'sys_match_id',
                                                    'match_date', 'date', 'tarih', 'match_time', 'time', 'saat', 'mac_saati', 'mac_tarihi',
                                                    'home_team', 'away_team', 'team_home', 'team_away', 'year', 'yil',
                                                    'ev_sahibi', 'deplasman', 'ev', 'misafir', 'ev_takim', 'misafir_takim',
                                                    'home_name', 'away_name', 'home', 'away', 'match_name', 'mac_adi',
                                                    'league', 'lig', 'league_name', 'lig_adi', 'country', 'ulke', 'country_name',
                                                    'created_at', 'updated_at', 'inserted_at'
                                                ];

                                                // 1. Remove blacklisted keys from the Saved Filter explicitly
                                                blacklist.forEach(b => delete filtersToSet[b]);

                                                // 2. Update filter with match data, BUT SKIP blacklisted columns
                                                Object.keys(filtersToSet).forEach(key => {
                                                    if (blacklist.includes(key)) return;

                                                    const val = selectedMatch.full_data[key];
                                                    if (val !== undefined && val !== null) {
                                                        filtersToSet[key] = String(val);
                                                    }
                                                });
                                            }
                                        }
                                        setFilters(filtersToSet);
                                    }
                                }
                            }}
                        >
                            <option value="">🎯 Filtre Seçin</option>
                            {savedFilters.map(f => (
                                <option key={f.id} value={f.id}>{f.filter_name}</option>
                            ))}
                        </select>

                        <button
                            className="premium-button btn-save"
                            onClick={handleSaveSavedFilter}
                        >
                            ⚡ Kaydet
                        </button>

                        <button
                            className="premium-button btn-delete"
                            onClick={handleDeleteSavedFilter}
                            disabled={!selectedFilterId}
                        >
                            ❌ Sil
                        </button>

                        <div className="divider"></div>

                        <button
                            className="premium-button btn-clear"
                            onClick={handleClearFilters}
                        >
                            🔄 Temizle
                        </button>

                        {/* Temporarily hidden - Tasarım Ayarları button
                        <button
                            className="premium-button btn-settings"
                            onClick={() => setShowSettings(!showSettings)}
                        >
                            {showSettings ? '⚙️ Ayarları Gizle' : '⚙️ Tasarım Ayarları'}
                        </button>
                        */}

                        <button
                            className="premium-button btn-column"
                            onClick={openColumnModal}
                        >
                            🔧 Sütun Filtrele
                        </button>
                    </div>
                </div>

                {/* Column Filter Side Panel */}
                {showColumnModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2000,
                        pointerEvents: 'none' // Allow seeing through and maybe clicking through (optional)? No, we want to catch clicks to close usually. 
                        // But if user wants to see table, let's keep it transparent.
                        // To allow closing by clicking outside, we need a transparent layer that IS clickable.
                    }}>
                        {/* Transparent Clickable Overlay for dismissal */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0)', // Completely transparent
                                pointerEvents: 'auto'
                            }}
                            onClick={() => setShowColumnModal(false)}
                        />

                        {/* The Right Drawer */}
                        <div className="drawer-panel" style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '420px',
                            maxWidth: '90vw',
                            background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
                            boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
                            display: 'flex',
                            flexDirection: 'column',
                            pointerEvents: 'auto',
                            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}>
                            <style>{`
                            @keyframes slideInRight {
                                from { transform: translateX(100%); opacity: 0; }
                                to { transform: translateX(0); opacity: 1; }
                            }
                            .drawer-panel {
                                border-left: 2px solid #ff6b35;
                            }
                            .custom-checkbox-label {
                                display: flex;
                                align-items: center;
                                gap: 12px;
                                padding: 12px 16px;
                                cursor: pointer;
                                border-radius: 10px;
                                transition: all 0.2s ease;
                                background: rgba(255,255,255,0.05);
                                border: 1px solid rgba(255,255,255,0.1);
                                margin-bottom: 8px;
                            }
                            .custom-checkbox-label:hover {
                                background: rgba(255,107,53,0.15);
                                border-color: #ff6b35;
                                transform: translateX(-2px);
                                box-shadow: 0 4px 12px rgba(255,107,53,0.2);
                            }
                            .custom-checkbox-label.disabled {
                                opacity: 0.5;
                                cursor: not-allowed;
                                background: rgba(255,255,255,0.02);
                            }
                            .custom-checkbox-label.disabled:hover {
                                transform: none;
                                border-color: rgba(255,255,255,0.1);
                            }
                            .custom-checkbox {
                                appearance: none;
                                width: 20px;
                                height: 20px;
                                border: 2px solid #ff6b35;
                                border-radius: 6px;
                                cursor: pointer;
                                position: relative;
                                transition: all 0.2s ease;
                                background: transparent;
                                flex-shrink: 0;
                            }
                            .custom-checkbox:checked {
                                background: #ff6b35;
                                border-color: #ff6b35;
                            }
                            .custom-checkbox:checked::after {
                                content: '✓';
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                color: white;
                                font-size: 12px;
                                font-weight: bold;
                            }
                            .checkbox-text {
                                font-size: 15px;
                                color: #fff;
                                font-weight: 500;
                            }
                            
                            /* Selected Tags Box styles */
                            .selected-tags-container {
                                background: rgba(0,0,0,0.3);
                                border-top: 1px solid rgba(255,107,53,0.3);
                                padding: 16px;
                                max-height: 150px;
                                overflow-y: auto;
                            }
                            .tag {
                                display: inline-flex;
                                align-items: center;
                                padding: 4px 10px;
                                background: rgba(255,107,53,0.2);
                                color: #ff6b35;
                                border-radius: 99px;
                                font-size: 12px;
                                font-weight: 600;
                                margin: 4px;
                                border: 1px solid rgba(255,107,53,0.4);
                            }
                        `}</style>

                            {/* Drawer Header */}
                            <div style={{
                                padding: '24px',
                                borderBottom: '1px solid rgba(255,107,53,0.3)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: '#ff6b35',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    🔧 Sütun Filtrele
                                    <span style={{
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        padding: '4px 10px',
                                        background: 'rgba(255,107,53,0.2)',
                                        borderRadius: '20px',
                                        color: '#ffa500',
                                        border: '1px solid rgba(255,107,53,0.4)'
                                    }}>
                                        {tempSelectedIds.length} Seçili
                                    </span>
                                </h3>
                                <button
                                    onClick={() => setShowColumnModal(false)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        fontSize: '24px',
                                        color: '#ff6b35',
                                        cursor: 'pointer',
                                        padding: '0 8px',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#ffa500'}
                                    onMouseLeave={(e) => e.target.style.color = '#ff6b35'}
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Select/Deselect All Buttons */}
                            <div style={{
                                padding: '10px 24px',
                                display: 'flex',
                                gap: '10px',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                background: 'rgba(0,0,0,0.2)'
                            }}>
                                <button
                                    onClick={handleSelectAll}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: '1px solid #ff6b35',
                                        background: 'rgba(255,107,53,0.15)',
                                        color: '#ff6b35',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(255,107,53,0.3)';
                                        e.target.style.borderColor = '#ffa500';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(255,107,53,0.15)';
                                        e.target.style.borderColor = '#ff6b35';
                                    }}
                                >
                                    ⚡ Tümünü Seç
                                </button>
                                <button
                                    onClick={handleDeselectAll}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        background: 'rgba(255,255,255,0.05)',
                                        color: '#ccc',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(255,255,255,0.1)';
                                        e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(255,255,255,0.05)';
                                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                                    }}
                                >
                                    ❌ Tümünü Kaldır
                                </button>
                            </div>

                            {/* Scrollable List - Card Grid Layout */}
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '20px',
                                background: 'rgba(0,0,0,0.15)'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                    gap: '10px'
                                }}>
                                    {allPotentialGroups.map(group => {
                                        const isSelected = tempSelectedIds.includes(intId(group.id));
                                        const isDefault = intId(group.id) === 1;

                                        return (
                                            <div
                                                key={group.id}
                                                onClick={() => !isDefault && toggleGroupSelection(group.id)}
                                                style={{
                                                    padding: '12px 8px',
                                                    borderRadius: '10px',
                                                    cursor: isDefault ? 'default' : 'pointer',
                                                    background: isSelected
                                                        ? 'linear-gradient(135deg, rgba(255,107,53,0.2) 0%, rgba(255,165,0,0.15) 100%)'
                                                        : 'rgba(255,255,255,0.05)',
                                                    border: isSelected
                                                        ? '2px solid #ff6b35'
                                                        : '2px solid rgba(255,255,255,0.1)',
                                                    boxShadow: isSelected
                                                        ? '0 3px 8px rgba(255, 107, 53, 0.3)'
                                                        : '0 2px 4px rgba(0,0,0,0.1)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    textAlign: 'center',
                                                    gap: '6px',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    opacity: isDefault ? 0.7 : 1,
                                                    transform: isSelected ? 'translateY(-2px)' : 'none',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isDefault) {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,107,53,0.25)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isDefault) {
                                                        e.currentTarget.style.transform = isSelected ? 'translateY(-2px)' : 'none';
                                                        e.currentTarget.style.boxShadow = isSelected
                                                            ? '0 3px 8px rgba(255, 107, 53, 0.3)'
                                                            : '0 2px 4px rgba(0,0,0,0.1)';
                                                    }
                                                }}
                                            >
                                                {/* Selection Indicator Dot */}
                                                {isSelected && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '6px',
                                                        right: '6px',
                                                        width: '6px',
                                                        height: '6px',
                                                        borderRadius: '50%',
                                                        background: '#ff6b35',
                                                        boxShadow: '0 0 0 2px rgba(255, 107, 53, 0.3)'
                                                    }} />
                                                )}

                                                <div style={{
                                                    fontSize: '20px',
                                                    marginBottom: '2px'
                                                }}>
                                                    {/* You could map icons here based on group text if available, or just use a generic one */}
                                                    {isSelected ? '🔥' : '⚪'}
                                                </div>

                                                <span style={{
                                                    fontSize: '12px',
                                                    fontWeight: isSelected ? '700' : '500',
                                                    color: isSelected ? '#ff6b35' : '#ccc',
                                                    lineHeight: '1.3'
                                                }}>
                                                    {group.text}
                                                </span>

                                                {isDefault && (
                                                    <span style={{
                                                        fontSize: '9px',
                                                        color: '#888',
                                                        marginTop: '2px',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        Varsayılan
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Selected Columns Visualization Box */}
                            <div className="selected-tags-container">
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#ff6b35', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Seçili Sütun Grupları
                                </div>
                                {tempSelectedIds.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                        {allPotentialGroups
                                            .filter(g => tempSelectedIds.includes(intId(g.id)))
                                            .map(g => (
                                                <span key={g.id} className="tag">
                                                    {g.text}
                                                </span>
                                            ))
                                        }
                                    </div>
                                ) : (
                                    <div style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
                                        Hiçbir sütun seçilmedi.
                                    </div>
                                )}
                            </div>

                            {/* Footer Actions */}
                            <div style={{
                                padding: '20px',
                                borderTop: '1px solid rgba(255,107,53,0.3)',
                                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                                display: 'flex',
                                gap: '12px'
                            }}>
                                <button
                                    onClick={() => setShowColumnModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        background: 'rgba(255,255,255,0.05)',
                                        color: '#ccc',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSaveColumnConfig}
                                    style={{
                                        flex: 2,
                                        padding: '12px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)'
                                    }}
                                >
                                    Kaydet ve Uygula
                                </button>
                            </div>

                        </div>
                    </div>
                )
                }

                {/* Design Settings Panel */}
                {
                    showSettings && (
                        <div style={{
                            padding: '28px',
                            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                            border: '2px solid rgba(255,107,53,0.3)',
                            borderRadius: '16px',
                            marginBottom: '24px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                            animation: 'slideDown 0.3s ease-out'
                        }}>
                            <style>{`
                        @keyframes slideDown {
                            from {
                                opacity: 0;
                                transform: translateY(-10px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                        .color-picker-wrapper {
                            display: flex;
                            flex-direction: column;
                            gap: 10px;
                            padding: 16px;
                            background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
                            border-radius: 12px;
                            border: 2px solid rgba(255,255,255,0.1);
                            transition: all 0.3s ease;
                        }
                        .color-picker-wrapper:hover {
                            border-color: #ff6b35;
                            transform: translateY(-2px);
                            box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
                        }
                        .color-label {
                            font-size: 13px;
                            font-weight: 600;
                            color: #fff;
                            text-transform: capitalize;
                            letter-spacing: 0.5px;
                        }
                        .color-input {
                            width: 100%;
                            height: 50px;
                            border: 3px solid rgba(255,255,255,0.2);
                            border-radius: 8px;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            background: transparent;
                        }
                        .color-input:hover {
                            border-color: #ff6b35;
                            box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.2);
                        }
                    `}</style>
                            <h3 style={{
                                margin: '0 0 20px 0',
                                fontSize: '20px',
                                fontWeight: '700',
                                background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                ⚙️ Renk Ayarları
                            </h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '16px'
                            }}>
                                {Object.keys(defaultColors).map(key => (
                                    <div key={key} className="color-picker-wrapper">
                                        <label className="color-label">{colorLabels[key] || key}</label>
                                        <input
                                            type="color"
                                            className="color-input"
                                            value={colors[key]}
                                            onChange={(e) => handleColorChange(key, e.target.value)}
                                        />
                                        <span style={{
                                            fontSize: '12px',
                                            color: '#888',
                                            fontFamily: 'monospace',
                                            textAlign: 'center'
                                        }}>
                                            {colors[key]}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                }

                {error && <div className="error-state">Hata: {error}</div>}

                <div style={{ position: "relative" }}>
                    <div className="table-container">
                        <table className="bookmaker-table" style={{
                            color: colors.rowText,
                            border: '3px solid #0a1b2a',
                            borderCollapse: 'collapse'
                        }}>
                            <thead style={{ backgroundColor: colors.headerBg }}>
                                {/* Group Headers - KEEPING THIS because it is the core feature of FiltreTablo */}
                                <tr>
                                    {columnGroups.map(group => (
                                        <th
                                            key={group.id}
                                            colSpan={group.columns.length}
                                            style={{
                                                color: colors.headerText,
                                                textAlign: 'center',
                                                borderRight: '2px solid #F6F1AD',
                                                borderBottom: '2px solid #F6F1AD',
                                                padding: '6px',
                                                fontSize: '14px',
                                                fontWeight: '800'
                                            }}
                                        >
                                            {group.text}
                                        </th>
                                    ))}
                                </tr>
                                {/* Column Headers + Filters - Matching TabloTest 'column-header-content' style */}
                                <tr>
                                    {columnGroups.map(group => (
                                        group.columns.map(col => (
                                            <th key={`${group.id}-${col.id}`} style={{
                                                color: colors.headerText,
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 10,
                                                borderRight: '2px solid #F6F1AD',
                                                borderBottom: '2px solid #F6F1AD',
                                                minWidth: col.width ? `${parseInt(col.width) + 60}px` : '220px',
                                                maxWidth: col.width ? `${parseInt(col.width) + 60}px` : '220px',
                                                textAlign: 'center',
                                                verticalAlign: 'bottom',
                                                padding: '8px'
                                            }}>
                                                <div className="column-header-content" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <span style={{ fontWeight: '700', fontSize: '11px', position: 'relative' }}>
                                                        <span
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            onClick={() => openTolerancePopup(col.name)}
                                                            title="Tıklayarak tolerans ayarlayın"
                                                        >
                                                            {col.text}
                                                            {rangeTolerance[col.name] && (rangeTolerance[col.name].minus > 0 || rangeTolerance[col.name].plus > 0) && (
                                                                <sup style={{ fontSize: '7px', color: '#ff6b35', marginLeft: '1px', fontWeight: '800' }}>
                                                                    -{rangeTolerance[col.name].minus}/+{rangeTolerance[col.name].plus}
                                                                </sup>
                                                            )}
                                                        </span>
                                                        {tolerancePopup === col.name && (
                                                            <div onClick={(e) => e.stopPropagation()} style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                zIndex: 999,
                                                                background: '#1a1a1a',
                                                                border: '1px solid #ff6b35',
                                                                borderRadius: '8px',
                                                                padding: '8px',
                                                                width: '150px',
                                                                boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                                                                marginTop: '4px'
                                                            }}>
                                                                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ fontSize: '9px', color: '#ff6b35', fontWeight: '700', marginBottom: '3px', textAlign: 'center' }}>− Eksi</div>
                                                                        <input
                                                                            type="text"
                                                                            value={toleranceInputs.minus}
                                                                            onChange={(e) => setToleranceInputs(prev => ({ ...prev, minus: e.target.value }))}
                                                                            placeholder="0.15"
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '4px',
                                                                                fontSize: '11px',
                                                                                fontWeight: '600',
                                                                                textAlign: 'center',
                                                                                background: '#2d2d2d',
                                                                                border: '1px solid #444',
                                                                                borderRadius: '4px',
                                                                                color: '#fff',
                                                                                outline: 'none',
                                                                                boxSizing: 'border-box'
                                                                            }}
                                                                            onKeyDown={(e) => e.key === 'Enter' && applyTolerance(col.name)}
                                                                        />
                                                                    </div>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ fontSize: '9px', color: '#4CAF50', fontWeight: '700', marginBottom: '3px', textAlign: 'center' }}>+ Artı</div>
                                                                        <input
                                                                            type="text"
                                                                            value={toleranceInputs.plus}
                                                                            onChange={(e) => setToleranceInputs(prev => ({ ...prev, plus: e.target.value }))}
                                                                            placeholder="0.20"
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '4px',
                                                                                fontSize: '11px',
                                                                                fontWeight: '600',
                                                                                textAlign: 'center',
                                                                                background: '#2d2d2d',
                                                                                border: '1px solid #444',
                                                                                borderRadius: '4px',
                                                                                color: '#fff',
                                                                                outline: 'none',
                                                                                boxSizing: 'border-box'
                                                                            }}
                                                                            onKeyDown={(e) => e.key === 'Enter' && applyTolerance(col.name)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                                    <button
                                                                        onClick={() => applyTolerance(col.name)}
                                                                        style={{
                                                                            flex: 1,
                                                                            padding: '4px',
                                                                            fontSize: '10px',
                                                                            fontWeight: '700',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            background: '#ff6b35',
                                                                            color: '#fff',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >Uygula</button>
                                                                    <button
                                                                        onClick={() => clearTolerance(col.name)}
                                                                        style={{
                                                                            flex: 1,
                                                                            padding: '4px',
                                                                            fontSize: '10px',
                                                                            fontWeight: '700',
                                                                            border: '1px solid #444',
                                                                            borderRadius: '4px',
                                                                            background: '#2d2d2d',
                                                                            color: '#888',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >Kapat</button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </span>
                                                    {(col.filterable === true || col.filterable === "true" || col.filterable === "T") && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                                            {(() => {
                                                                const currentMode = filterModes[col.name] || 'default';
                                                                const style = getFilterModeStyle(currentMode);
                                                                return (
                                                                    <>
                                                                        {/* Temporarily hidden - Filter Mode Dropdown
                                                                        <select
                                                                            className="filter-mode-select"
                                                                            value={currentMode}
                                                                            onChange={(e) => handleFilterModeChange(col.name, e.target.value)}
                                                                            style={{
                                                                                appearance: 'none',
                                                                                WebkitAppearance: 'none',
                                                                                MozAppearance: 'none',
                                                                                padding: '4px 8px',
                                                                                fontSize: '12px',
                                                                                fontWeight: '600',
                                                                                width: '100%',
                                                                                height: '28px',
                                                                                border: style.border,
                                                                                borderRadius: '6px',
                                                                                cursor: 'pointer',
                                                                                background: style.bg,
                                                                                textAlign: 'center',
                                                                                color: style.color,
                                                                                transition: 'all 0.2s',
                                                                                outline: 'none',
                                                                                boxShadow: currentMode !== 'default' ? style.shadow : 'none'
                                                                            }}
                                                                            title="Filtreleme Modu"
                                                                        >
                                                                            <option value="default" style={{ background: '#fff', color: '#000' }}>Tümü</option>
                                                                            <option value="eq" style={{ background: '#d4edda', color: '#155724' }}>= Eşittir</option>
                                                                            <option value="range" style={{ background: '#cce5ff', color: '#004085' }}>Aralık</option>
                                                                            <option value="lt" style={{ background: '#fff3cd', color: '#856404' }}>&lt; Küçüktür</option>
                                                                            <option value="gt" style={{ background: '#f8d7da', color: '#721c24' }}>&gt; Büyüktür</option>
                                                                        </select>
                                                                        */}

                                                                        {/* Filter Input - Below Mode */}
                                                                        <div style={{ position: 'relative', width: '100%' }}>
                                                                            <input
                                                                                type="text"
                                                                                className="filter-input"
                                                                                placeholder={
                                                                                    currentMode === 'range' ? '' :
                                                                                        currentMode === 'lt' ? '' :
                                                                                            currentMode === 'gt' ? '' :
                                                                                                currentMode === 'eq' ? '' :
                                                                                                    ''
                                                                                }
                                                                                value={filters[col.name] || ''}
                                                                                onChange={(e) => handleFilterChange(col.name, e.target.value)}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    padding: '6px 26px 6px 10px',
                                                                                    color: '#495057',
                                                                                    fontSize: '13px',
                                                                                    fontWeight: '500',
                                                                                    borderRadius: '6px',
                                                                                    border: currentMode !== 'default' ? style.border : '1px solid #ced4da',
                                                                                    backgroundColor: '#fff',
                                                                                    transition: 'all 0.2s',
                                                                                    boxShadow: currentMode !== 'default' ? style.shadow : 'none',
                                                                                    height: '32px'
                                                                                }}
                                                                            />
                                                                            {filters[col.name] && (
                                                                                <button
                                                                                    onClick={() => handleFilterChange(col.name, '')}
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        right: '6px',
                                                                                        top: '50%',
                                                                                        transform: 'translateY(-50%)',
                                                                                        background: 'rgba(220, 53, 69, 0.1)',
                                                                                        borderRadius: '50%',
                                                                                        width: '20px',
                                                                                        height: '20px',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        border: 'none',
                                                                                        color: '#dc3545',
                                                                                        cursor: 'pointer',
                                                                                        fontSize: '11px',
                                                                                        fontWeight: '700',
                                                                                        padding: 0,
                                                                                        transition: 'all 0.2s'
                                                                                    }}
                                                                                    onMouseEnter={(e) => {
                                                                                        e.target.style.background = '#dc3545';
                                                                                        e.target.style.color = '#fff';
                                                                                    }}
                                                                                    onMouseLeave={(e) => {
                                                                                        e.target.style.background = 'rgba(220, 53, 69, 0.1)';
                                                                                        e.target.style.color = '#dc3545';
                                                                                    }}
                                                                                    title="Temizle"
                                                                                >
                                                                                    ✕
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </th>
                                        ))
                                    ))}
                                </tr>
                                {/* End Column Headers */}
                            </thead>
                            <tbody>
                                {(() => {
                                    let displayMatches = matches;
                                    if (selectedMatchId) {
                                        const foundIndex = matches.findIndex(m => String(m.id) === String(selectedMatchId));
                                        if (foundIndex > -1) {
                                            const m = matches[foundIndex];
                                            displayMatches = [m, ...matches.filter((_, i) => i !== foundIndex)];
                                        }
                                    }

                                    return displayMatches.map((match, index) => {
                                        const rowStyle = {
                                            backgroundColor: index % 2 === 0 ? colors.rowOddBg : colors.rowEvenBg,
                                        };
                                        const isLast = index === matches.length - 1;

                                        return (
                                            <tr
                                                key={match.id || index}
                                                style={rowStyle}
                                                ref={isLast ? lastMatchElementRef : null}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hoverBg}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowStyle.backgroundColor}
                                            >
                                                {columnGroups.map(group => (
                                                    group.columns.map(col => (
                                                        <td
                                                            key={`${match.id}-${col.id}`}
                                                            style={{
                                                                cursor: 'pointer',
                                                                borderRight: '1px solid rgba(246, 241, 173, 0.3)',
                                                                borderBottom: '1px solid rgba(246, 241, 173, 0.3)',
                                                                padding: '8px'
                                                            }}
                                                            onDoubleClick={() => {
                                                                const content = getCellContent(match, col.name);
                                                                if (content !== '-' && content !== null && content !== undefined) {
                                                                    handleFilterChange(col.name, String(content));
                                                                }
                                                            }}
                                                            title="Filtrelemek için çift tıklayın"
                                                        >
                                                            {getCellContent(match, col.name)}
                                                        </td>
                                                    ))
                                                ))}
                                            </tr>
                                        );
                                    });
                                })()}
                                {matches.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="100%" className="empty-state" style={{ textAlign: 'center', padding: '20px' }}>
                                            Veri bulunamadı.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Watermark */}
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        pointerEvents: "none",
                        zIndex: 2,
                        userSelect: "none"
                    }} />
                </div>
            </div >
        </>
    );
};

export default YabanciAcilis;
